import type { CSSProperties } from 'react';
import { CalendarRange, ConciergeBell, CreditCard, DoorOpen, FileText, Globe, IdCard, Landmark, Smartphone, type LucideIcon } from 'lucide-react';

export type ModuleKey = 'memberships' | 'payments' | 'access' | 'classes' | 'desk' | 'finance' | 'website' | 'members' | 'documents';

type Localized = { en: string; ka: string };

export interface BulkModule {
  key: ModuleKey;
  name: Localized;
  job: Localized;
  icon: LucideIcon;
  color: string;
  soft: string;
  ink: string;
}

export const MODULES: BulkModule[] = [
  {
    key: 'memberships',
    name: { en: 'Memberships', ka: 'აბონემენტები' },
    job: { en: 'Plans, pauses and renewals', ka: 'პაკეტები, შეჩერება და განახლება' },
    icon: IdCard,
    color: '#c86265',
    soft: '#ffebea',
    ink: '#8e3d40',
  },
  {
    key: 'payments',
    name: { en: 'Payments', ka: 'გადახდები' },
    job: { en: 'Card payments, saved cards and refunds', ka: 'ბარათით გადახდა, შენახული ბარათები და დაბრუნება' },
    icon: CreditCard,
    color: '#5685d4',
    soft: '#e8f1fe',
    ink: '#345a98',
  },
  {
    key: 'access',
    name: { en: 'Access', ka: 'დაშვება' },
    job: { en: 'Turnstiles, cards and a live door feed', ka: 'ტურნიკეტები, ბარათები და შესვლები რეალურ დროში' },
    icon: DoorOpen,
    color: '#58994a',
    soft: '#e6f6e3',
    ink: '#36692a',
  },
  {
    key: 'classes',
    name: { en: 'Classes', ka: 'ჯგუფური ვარჯიშები' },
    job: { en: 'Schedules, bookings and waitlists', ka: 'განრიგი, ჯავშნები და მოლოდინის სია' },
    icon: CalendarRange,
    color: '#9372c8',
    soft: '#f2edfe',
    ink: '#654a8f',
  },
  {
    key: 'desk',
    name: { en: 'Front desk', ka: 'მიმღები' },
    job: { en: 'Check-ins, desk sales and cash shifts', ka: 'შესვლის აღრიცხვა, გაყიდვები და სალარო' },
    icon: ConciergeBell,
    color: '#d5863e',
    soft: '#ffecde',
    ink: '#864900',
  },
  {
    key: 'finance',
    name: { en: 'Finance', ka: 'ფინანსები' },
    job: { en: 'Ledger, daily report and statements', ka: 'რეესტრი, დღის ანგარიში და ამონაწერები' },
    icon: Landmark,
    color: '#039c89',
    soft: '#dcf7f1',
    ink: '#056a5d',
  },
  {
    key: 'website',
    name: { en: 'Website', ka: 'ვებსაიტი' },
    job: { en: 'Your own site on your own domain', ka: 'თქვენი საიტი, თქვენს დომენზე' },
    icon: Globe,
    color: '#0695b5',
    soft: '#dbf5fe',
    ink: '#00657c',
  },
  {
    key: 'members',
    name: { en: 'Member area', ka: 'წევრის კაბინეტი' },
    job: { en: 'Members buy, book and pay online', ka: 'წევრები ყიდულობენ, ჯავშნიან და იხდიან ონლაინ' },
    icon: Smartphone,
    color: '#b9649f',
    soft: '#ffe9f7',
    ink: '#823f6e',
  },
  {
    key: 'documents',
    name: { en: 'Documents', ka: 'დოკუმენტები' },
    job: { en: 'Terms members accept before paying', ka: 'პირობები, რომლებსაც წევრები გადახდამდე ეთანხმებიან' },
    icon: FileText,
    color: '#ae9c2a',
    soft: '#f4f1da',
    ink: '#685b00',
  },
];

const BY_KEY = new Map(MODULES.map((m) => [m.key, m]));

export function getModule(key: ModuleKey): BulkModule {
  return BY_KEY.get(key)!;
}

export function moduleStyle(key: ModuleKey | null | undefined): CSSProperties | undefined {
  if (!key) return undefined;
  const m = getModule(key);
  return { '--mod': m.color, '--mod-soft': m.soft, '--mod-ink': m.ink } as CSSProperties;
}

const ADMIN_ROUTES: [string, ModuleKey][] = [
  ['/admin/reception', 'desk'],
  ['/admin/bar', 'desk'],
  ['/admin/members', 'memberships'],
  ['/admin/segments', 'memberships'],
  ['/admin/targets', 'memberships'],
  ['/admin/corporate', 'memberships'],
  ['/admin/memberships', 'memberships'],
  ['/admin/plans', 'memberships'],
  ['/admin/classes', 'classes'],
  ['/admin/sessions', 'classes'],
  ['/admin/bookings', 'classes'],
  ['/admin/trainers', 'classes'],
  ['/admin/appointments', 'classes'],
  ['/admin/payments', 'payments'],
  ['/admin/access', 'access'],
  ['/admin/backoffice', 'finance'],
  ['/admin/website', 'website'],
  ['/admin/content', 'documents'],
];

export function moduleForPath(pathname: string): ModuleKey | null {
  const match = ADMIN_ROUTES.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return match ? match[1] : null;
}
